import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Pill, Activity, Dumbbell, Utensils, ListChecks, Plus, Check, Clock,
  AlertTriangle, Trash2, Pause, Play, Pencil, X, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FormField } from "@/components/common/FormSection";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORY_META = {
  medication: { label: "Medication", icon: Pill, color: "#0B7E8C" },
  reading: { label: "Reading / Vitals", icon: Activity, color: "#2563EB" },
  exercise: { label: "Exercise", icon: Dumbbell, color: "#7C3AED" },
  meal: { label: "Meal", icon: Utensils, color: "#D97706" },
  other: { label: "Other", icon: ListChecks, color: "#64748B" },
};
const STATUS_META = {
  done: { label: "Done", bg: "#DCFCE7", text: "#15803D" },
  pending: { label: "Pending", bg: "#EFF6FF", text: "#1D4ED8" },
  missed: { label: "Missed", bg: "#FEE2E2", text: "#B91C1C" },
};
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const EMPTY = {
  title: "", category: "medication", instructions: "",
  frequency: { type: "daily", times: ["08:00"], days_of_week: [1, 3, 5], day_of_month: 1 },
  start_date: new Date().toISOString().slice(0, 10), end_date: "", grace_minutes: 60,
};

function CatIcon({ category, size = 16 }) {
  const m = CATEGORY_META[category] || CATEGORY_META.other;
  const I = m.icon;
  return <I style={{ color: m.color }} width={size} height={size} />;
}

function StatusChip({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
      style={{ background: m.bg, color: m.text }} data-testid={`ct-status-${status}`}>{m.label}</span>
  );
}

function dateLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function CareTasksTab({ patientId, canManage }) {
  const { user } = useAuth();
  const myName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "You";
  const [tasks, setTasks] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("schedule");
  const [dialog, setDialog] = useState({ open: false, editing: null, form: EMPTY });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(`/care-tasks/scan`, null, { params: { patient_id: patientId } }).catch(() => {});
      const [t, o] = await Promise.all([
        api.get(`/care-tasks/patients/${patientId}/tasks`),
        api.get(`/care-tasks/patients/${patientId}/occurrences`),
      ]);
      setTasks(t.data.items || []);
      setOccurrences(o.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    return tasks.reduce((a, t) => {
      const s = t.stats || {};
      a.done += s.done || 0; a.pending += s.pending || 0; a.missed += s.missed || 0;
      return a;
    }, { done: 0, pending: 0, missed: 0 });
  }, [tasks]);
  const adherence = useMemo(() => {
    const resolved = totals.done + totals.missed;
    return resolved ? Math.round((totals.done / resolved) * 100) : null;
  }, [totals]);

  const grouped = useMemo(() => {
    const g = {};
    for (const o of occurrences) (g[o.scheduled_date] = g[o.scheduled_date] || []).push(o);
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [occurrences]);

  function openCreate() {
    if (!canManage) return;
    setDialog({ open: true, editing: null, form: { ...EMPTY, frequency: { ...EMPTY.frequency, times: ["08:00"] } } });
  }
  function openEdit(t) {
    setDialog({
      open: true, editing: t,
      form: {
        title: t.title, category: t.category, instructions: t.instructions || "",
        frequency: {
          type: t.frequency?.type || "daily",
          times: t.frequency?.times?.length ? [...t.frequency.times] : ["08:00"],
          days_of_week: t.frequency?.days_of_week?.length ? [...t.frequency.days_of_week] : [1, 3, 5],
          day_of_month: t.frequency?.day_of_month || 1,
        },
        start_date: t.start_date, end_date: t.end_date || "", grace_minutes: t.grace_minutes || 60,
      },
    });
  }

  const setF = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));
  const setFreq = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, frequency: { ...d.form.frequency, ...patch } } }));

  async function save() {
    const f = dialog.form;
    if (!f.title.trim()) return toast.error("Enter a title");
    if (!f.frequency.times.length || f.frequency.times.some((x) => !x)) return toast.error("Add at least one valid time");
    if (f.frequency.type === "weekly" && !f.frequency.days_of_week.length) return toast.error("Select day(s) of week");
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(), category: f.category, instructions: f.instructions || null,
        frequency: {
          type: f.frequency.type,
          times: f.frequency.times,
          days_of_week: f.frequency.type === "weekly" ? f.frequency.days_of_week : [],
          day_of_month: f.frequency.type === "monthly" ? Number(f.frequency.day_of_month) : null,
        },
        start_date: f.start_date, end_date: f.end_date || null, grace_minutes: Number(f.grace_minutes) || 60,
      };
      if (dialog.editing) {
        await api.put(`/care-tasks/tasks/${dialog.editing.id}`, payload);
        toast.success("Reminder updated");
      } else {
        await api.post(`/care-tasks/patients/${patientId}/tasks`, payload);
        toast.success("Reminder created — care team notified");
      }
      setDialog({ open: false, editing: null, form: EMPTY });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  // Patch a single occurrence + its task stats locally (no refetch → no flicker).
  function applyOccChange(occId, taskId, fromStatus, toStatus, patch = {}) {
    setOccurrences((prev) => prev.map((o) => (o.id === occId ? { ...o, status: toStatus, ...patch } : o)));
    setTasks((prev) => prev.map((t) => {
      if (t.id !== taskId) return t;
      const s = { done: 0, pending: 0, missed: 0, ...(t.stats || {}) };
      if (fromStatus in s) s[fromStatus] = Math.max(0, (s[fromStatus] || 0) - 1);
      if (toStatus in s) s[toStatus] = (s[toStatus] || 0) + 1;
      const resolved = s.done + s.missed;
      s.adherence = resolved ? Math.round((s.done / resolved) * 100) : null;
      return { ...t, stats: s };
    }));
  }

  async function markDone(occ) {
    if (occ.status === "done") return;
    const from = occ.status;
    applyOccChange(occ.id, occ.task_id, from, "done", { done_by_name: myName, done_at: new Date().toISOString(), done_source: "nurse" });
    try {
      const r = await api.post(`/care-tasks/occurrences/${occ.id}/complete`, { source: "nurse" });
      setOccurrences((prev) => prev.map((o) => (o.id === occ.id ? { ...o, ...r.data } : o)));
    } catch (e) {
      toast.error(formatApiError(e));
      applyOccChange(occ.id, occ.task_id, "done", from, { done_by_name: null, done_at: null, done_source: null });
    }
  }
  async function reopen(occ) {
    if (occ.status !== "done") return;
    applyOccChange(occ.id, occ.task_id, "done", "pending", { done_by_name: null, done_at: null, done_source: null });
    try {
      await api.post(`/care-tasks/occurrences/${occ.id}/reopen`);
    } catch (e) {
      toast.error(formatApiError(e));
      applyOccChange(occ.id, occ.task_id, "pending", "done", { done_by_name: myName });
    }
  }
  async function toggleStatus(t) {
    try {
      await api.put(`/care-tasks/tasks/${t.id}`, { status: t.status === "active" ? "paused" : "active" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function removeTask(t) {
    try {
      await api.delete(`/care-tasks/tasks/${t.id}`);
      toast.success("Reminder ended");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-4" data-testid="care-tasks-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-[#1F2937]">Care Reminders</h3>
          <p className="text-xs text-[#6B7280]">Medication, readings, exercise & meals — synced to the patient app. Adherence tracked per occurrence.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ct-new-reminder">
            <Plus className="w-4 h-4 mr-1.5" /> New Reminder
          </Button>
        )}
      </div>

      {/* adherence summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="hah-card p-3" data-testid="ct-kpi-adherence">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Adherence</div>
          <div className="text-2xl font-bold mt-0.5 text-[#0B7E8C]">{adherence === null ? "—" : `${adherence}%`}</div>
        </div>
        <div className="hah-card p-3" data-testid="ct-kpi-done">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Done</div>
          <div className="text-2xl font-bold mt-0.5 text-[#15803D]">{totals.done}</div>
        </div>
        <div className="hah-card p-3" data-testid="ct-kpi-pending">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Pending</div>
          <div className="text-2xl font-bold mt-0.5 text-[#1D4ED8]">{totals.pending}</div>
        </div>
        <div className="hah-card p-3" data-testid="ct-kpi-missed">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Missed</div>
          <div className="text-2xl font-bold mt-0.5 text-[#B91C1C]">{totals.missed}</div>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-[#F5F7FA]">
          <TabsTrigger value="schedule" data-testid="ct-tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="ct-tab-reminders">Reminders ({tasks.length})</TabsTrigger>
        </TabsList>

        {/* SCHEDULE / OCCURRENCES */}
        <TabsContent value="schedule" className="mt-4">
          {loading ? <div className="text-sm text-[#6B7280] p-6 text-center">Loading…</div> :
            grouped.length === 0 ? (
              <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No scheduled reminders. {canManage && "Create one to start tracking adherence."}</div>
            ) : (
              <div className="space-y-4">
                {grouped.map(([day, list]) => (
                  <div key={day} data-testid={`ct-day-${day}`}>
                    <div className="text-xs font-semibold text-[#475569] mb-1.5">{dateLabel(day)} <span className="text-[#94A3B8] font-normal">· {day}</span></div>
                    <div className="hah-card divide-y divide-[#F1F5F9]">
                      {list.map((o) => (
                        <div key={o.id} className="flex items-center gap-3 p-2.5" data-testid={`ct-occ-${o.id}`}>
                          <CatIcon category={o.category} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1F2937] truncate">{o.title}</div>
                            <div className="text-xs text-[#6B7280] flex items-center gap-1"><Clock className="w-3 h-3" /> {o.scheduled_time}
                              {o.done_by_name && <span className="ml-1 text-[#94A3B8]">· {o.done_by_name}</span>}</div>
                          </div>
                          <StatusChip status={o.status} />
                          {canManage && o.status !== "done" && (
                            <Button size="sm" variant="outline" className="h-7 border-[#15803D] text-[#15803D] hover:bg-[#DCFCE7]"
                              onClick={() => markDone(o)} data-testid={`ct-done-${o.id}`}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Done
                            </Button>
                          )}
                          {canManage && o.status === "done" && (
                            <Button size="sm" variant="ghost" className="h-7 text-[#6B7280]" onClick={() => reopen(o)} data-testid={`ct-reopen-${o.id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </TabsContent>

        {/* TASK DEFINITIONS */}
        <TabsContent value="reminders" className="mt-4">
          {loading ? <div className="text-sm text-[#6B7280] p-6 text-center">Loading…</div> :
            tasks.length === 0 ? (
              <div className="hah-card p-8 text-center text-sm text-[#6B7280]">No reminders yet.</div>
            ) : (
              <div className="space-y-2.5">
                {tasks.map((t) => {
                  const m = CATEGORY_META[t.category] || CATEGORY_META.other;
                  return (
                    <div key={t.id} className="hah-card p-3.5 flex items-start gap-3" data-testid={`ct-task-${t.id}`}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.color}1A` }}>
                        <CatIcon category={t.category} size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#1F2937]">{t.title}</span>
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: `${m.color}1A`, color: m.color }}>{m.label}</span>
                          {t.status !== "active" && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">{t.status}</span>}
                        </div>
                        <div className="text-xs text-[#6B7280] mt-0.5">{t.frequency_label}</div>
                        {t.instructions && <div className="text-xs text-[#94A3B8] mt-0.5 italic">{t.instructions}</div>}
                        <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                          <span className="text-[#15803D] font-medium">{t.stats?.done || 0} done</span>
                          <span className="text-[#1D4ED8] font-medium">{t.stats?.pending || 0} pending</span>
                          <span className="text-[#B91C1C] font-medium">{t.stats?.missed || 0} missed</span>
                          {t.stats?.adherence !== null && t.stats?.adherence !== undefined && <span className="text-[#6B7280]">· {t.stats.adherence}% adherence</span>}
                        </div>
                      </div>
                      {canManage && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`ct-edit-${t.id}`} title="Edit"><Pencil className="w-4 h-4 text-[#6B7280]" /></Button>
                          {t.status !== "ended" && (
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleStatus(t)} data-testid={`ct-toggle-${t.id}`} title={t.status === "active" ? "Pause" : "Resume"}>
                              {t.status === "active" ? <Pause className="w-4 h-4 text-[#6B7280]" /> : <Play className="w-4 h-4 text-[#15803D]" />}
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`ct-delete-${t.id}`} title="End"
                            onClick={() => setConfirm({ open: true, title: "End reminder", description: `End "${t.title}"? Future occurrences are removed; history is kept.`, destructive: true, onConfirm: () => removeTask(t) })}>
                            <Trash2 className="w-4 h-4 text-[#EF4444]" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </TabsContent>
      </Tabs>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="ct-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? "Edit reminder" : "New reminder"}</DialogTitle>
            <DialogDescription>The patient sees this in their app; the care team is notified on create, completion and missed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <FormField label="What should the patient do?" required>
              <Input value={dialog.form.title} onChange={(e) => setF({ title: e.target.value })} placeholder="e.g. Take Metformin 500mg" data-testid="ct-input-title" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Category">
                <Select value={dialog.form.category} onValueChange={(v) => setF({ category: v })}>
                  <SelectTrigger data-testid="ct-input-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Frequency">
                <Select value={dialog.form.frequency.type} onValueChange={(v) => setFreq({ type: v })}>
                  <SelectTrigger data-testid="ct-input-freq-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Every day</SelectItem>
                    <SelectItem value="weekly">Specific weekdays</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            {dialog.form.frequency.type === "weekly" && (
              <FormField label="Days of week" required>
                <div className="flex gap-1.5 flex-wrap" data-testid="ct-days">
                  {DAYS.map((d, i) => {
                    const on = dialog.form.frequency.days_of_week.includes(i);
                    return (
                      <button key={i} type="button" data-testid={`ct-day-${i}`}
                        onClick={() => setFreq({ days_of_week: on ? dialog.form.frequency.days_of_week.filter((x) => x !== i) : [...dialog.form.frequency.days_of_week, i].sort() })}
                        className={`w-10 h-8 rounded-md text-xs font-medium border ${on ? "bg-[#0B7E8C] text-white border-[#0B7E8C]" : "bg-white text-[#6B7280] border-[#E5E7EB]"}`}>{d}</button>
                    );
                  })}
                </div>
              </FormField>
            )}
            {dialog.form.frequency.type === "monthly" && (
              <FormField label="Day of month" required>
                <Input type="number" min="1" max="31" value={dialog.form.frequency.day_of_month} onChange={(e) => setFreq({ day_of_month: e.target.value })} data-testid="ct-input-dom" />
              </FormField>
            )}

            <FormField label="Time(s) of day" required>
              <div className="space-y-2" data-testid="ct-times">
                {dialog.form.frequency.times.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="time" value={t} data-testid={`ct-time-${i}`}
                      onChange={(e) => { const next = [...dialog.form.frequency.times]; next[i] = e.target.value; setFreq({ times: next }); }} className="w-40" />
                    {dialog.form.frequency.times.length > 1 && (
                      <button type="button" onClick={() => setFreq({ times: dialog.form.frequency.times.filter((_, idx) => idx !== i) })} className="text-[#EF4444]" data-testid={`ct-time-remove-${i}`}><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <Button type="button" size="sm" variant="outline" className="border-dashed" data-testid="ct-add-time"
                  onClick={() => setFreq({ times: [...dialog.form.frequency.times, "12:00"] })}><Plus className="w-3.5 h-3.5 mr-1" /> Add time</Button>
              </div>
            </FormField>

            <FormField label="Instructions (optional)">
              <Textarea rows={2} value={dialog.form.instructions} onChange={(e) => setF({ instructions: e.target.value })} placeholder="e.g. Take after food" data-testid="ct-input-instructions" />
            </FormField>

            <div className="grid grid-cols-3 gap-3">
              <FormField label="Start date" required>
                <Input type="date" value={dialog.form.start_date} onChange={(e) => setF({ start_date: e.target.value })} data-testid="ct-input-start" />
              </FormField>
              <FormField label="End date">
                <Input type="date" value={dialog.form.end_date} onChange={(e) => setF({ end_date: e.target.value })} data-testid="ct-input-end" />
              </FormField>
              <FormField label="Grace (min)" hint="Auto-missed after">
                <Input type="number" min="0" value={dialog.form.grace_minutes} onChange={(e) => setF({ grace_minutes: e.target.value })} data-testid="ct-input-grace" />
              </FormField>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog((d) => ({ ...d, open: false }))} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="ct-save">
              {saving ? "Saving…" : dialog.editing ? "Save changes" : "Create reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={confirm.open} onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title={confirm.title} description={confirm.description} destructive={confirm.destructive}
        onConfirm={() => { confirm.onConfirm?.(); setConfirm({ open: false }); }} />
    </div>
  );
}
